import React from "react";
import { Link } from "react-router-dom";
import { KpiReportResponse, MonthlyKpiStats } from "../../api/kpi";
import { ROUTES } from "../../config/routes";
import { formatCurrency, formatInteger } from "../../helpers/formatNumber";
import {
  deltaLabel,
  excludeCurrentMonth,
  formatMonthLabel,
  formatOptional,
  latestValue,
  monthKey,
  selectPositiveFirst,
  toMonthlySeries,
  trailing,
} from "../../helpers/dashboardMetrics";
import { SERIES_COLORS } from "../AnalyticsPlus/activityCharts";
import { formatSessionDuration } from "../../helpers/analyticsPlus";
import { activitySeries } from "./useMonthlyActivity";
import { MonthlyActivity } from "./useDashboardData";
import { StatTile } from "./StatTile";
import { TrendCard } from "./TrendCard";

// A year of context is enough to read a trend without squeezing the axis.
const MONTHS_SHOWN = 12;

// Slots in the stat grid. Fewer measures than this may be growing in a given
// month, in which case the remaining slots take the best of the rest.
const TILE_COUNT = 4;

// A tile is a single-series sparkline in its own card, so its colour carries no
// identity — the label does. Measures are coloured by family instead, which is
// the one thing a repeated hue can usefully mean here.
const PEOPLE_COLOR = SERIES_COLORS[0];
const MONEY_COLOR = SERIES_COLORS[3];
const ACTIVITY_COLOR = SERIES_COLORS[4];
const GAMES_COLOR = SERIES_COLORS[5];

// Floors for the month a percentage is measured from, per unit.
const COUNT_BASE = 100; // events or people
const AMOUNT_BASE = 100_000; // øre, i.e. 1.000 kr
const SECONDS_BASE = 10; // a session shorter than this is not a session
const PERCENT_BASE = 1; // a rate below 1% is noise to measure change from

// Amounts arrive in øre. Axis gutters cannot fit a full kroner figure, so ticks
// are abbreviated while the tooltip keeps the exact number.
const krTick = (ore: number): string => {
  const kr = ore / 100;
  if (Math.abs(kr) >= 1_000_000) return `${(kr / 1_000_000).toFixed(1)}M`;
  if (Math.abs(kr) >= 1_000) return `${Math.round(kr / 1_000)}k`;
  return `${Math.round(kr)}`;
};

interface KpiHighlightsProps {
  data: KpiReportResponse;
  activity: MonthlyActivity | null;
}

const KpiHighlights: React.FC<KpiHighlightsProps> = ({ data, activity }) => {
  // Same reason as the activity figures: `response.data` is untyped, so neither
  // collection is guaranteed to arrive however KpiReportResponse describes it.
  const allTime = data.allTimeStats ?? ({} as typeof data.allTimeStats);
  // The month in progress is dropped before anything is derived from it. Its
  // bucket holds only the days elapsed so far, which drew a cliff on both
  // charts and turned every month-over-month delta into a fake collapse for
  // the first days of a month. All-time totals below still include today.
  const monthly = excludeCurrentMonth(data.monthlyStats ?? [], new Date());

  const seriesFor = (pick: (stat: MonthlyKpiStats) => number) =>
    trailing(toMonthlySeries(monthly, pick), MONTHS_SHOWN);

  const series = {
    totalUsers: seriesFor((s) => s.totalUsers),
    activeUsers: seriesFor((s) => s.activeUsers),
    newUsers: seriesFor((s) => s.newUsers),
    amount: seriesFor((s) => s.totalTransactionAmount),
    cashback: seriesFor((s) => s.totalAssignedCashback),
    transactionCount: seriesFor((s) => s.transactionCount),
    purchasingUsers: seriesFor((s) => s.uniquePurchasingUsers),
    downsizingAmount: seriesFor((s) => s.totalDownsizingAmount),
    downsizingUsers: seriesFor((s) => s.uniqueDownsizingUsers),
    gamePlays: seriesFor((s) => s.totalGamesPlayed),
    gamePlayers: seriesFor((s) => s.uniqueGamePlayers),
    gamesWon: seriesFor((s) => s.totalGamesWon),
    rewardAmount: seriesFor((s) => s.totalRewardAmount),
  };

  const kr = (ore: number) => `${formatCurrency(ore)} kr`;
  const today = new Date();

  // Every series covers the same months, so one caption and one note describe
  // all of them. Both name the period rather than saying "previous month",
  // which would be wrong for most of any given month.
  const comparison = deltaLabel(series.amount);
  const caption = comparison ? `all time · ${comparison}` : "all time";
  const chartNote = `Complete months only · ${formatMonthLabel(
    monthKey(new Date())
  )} is still in progress`;

  // Every measure the KPI report carries, in the order slots are preferred.
  // The first four are the section's usual face; the rest only appear when one
  // of those declined in the latest complete month.
  //
  // minBase is the previous month a percentage is measured from. Below it the
  // measure is not treated as growing, because a percentage off a tiny base
  // describes nothing: in the sample data game plays go 16 -> 1410 and read
  // +8713%, which would otherwise outrank every real measure on the page.
  // COUNT_BASE is in events, AMOUNT_BASE in øre (1.000 kr).
  const candidates = [
    {
      label: "Total users",
      value: formatOptional(allTime.totalUsers, formatInteger),
      series: series.totalUsers,
      color: PEOPLE_COLOR,
      minBase: COUNT_BASE,
    },
    {
      label: "Active users",
      value: formatOptional(allTime.totalActiveUsers, formatInteger),
      series: series.activeUsers,
      color: PEOPLE_COLOR,
      minBase: COUNT_BASE,
    },
    {
      label: "Transaction amount",
      value: formatOptional(allTime.totalTransactionAmount, kr),
      series: series.amount,
      color: MONEY_COLOR,
      minBase: AMOUNT_BASE,
    },
    {
      label: "Assigned cashback",
      value: formatOptional(allTime.totalAssignedCashback, kr),
      series: series.cashback,
      color: MONEY_COLOR,
      minBase: AMOUNT_BASE,
    },
    {
      label: "Transactions",
      value: formatOptional(allTime.totalTransactionCount, formatInteger),
      series: series.transactionCount,
      color: ACTIVITY_COLOR,
      minBase: COUNT_BASE,
    },
    {
      label: "Purchasing users",
      value: formatOptional(allTime.totalUniquePurchasingUsers, formatInteger),
      series: series.purchasingUsers,
      color: PEOPLE_COLOR,
      minBase: COUNT_BASE,
    },
    {
      label: "Downsizing amount",
      value: formatOptional(allTime.totalDownsizingAmount, kr),
      series: series.downsizingAmount,
      color: MONEY_COLOR,
      minBase: AMOUNT_BASE,
    },
    {
      label: "Downsizing users",
      value: formatOptional(allTime.totalUniqueDownsizingUsers, formatInteger),
      series: series.downsizingUsers,
      color: PEOPLE_COLOR,
      minBase: COUNT_BASE,
    },
    {
      label: "Game plays",
      value: formatOptional(allTime.totalGamesPlayed, formatInteger),
      series: series.gamePlays,
      color: GAMES_COLOR,
      minBase: COUNT_BASE,
    },
    {
      label: "Game players",
      value: formatOptional(allTime.totalUniqueGamePlayers, formatInteger),
      series: series.gamePlayers,
      color: GAMES_COLOR,
      minBase: COUNT_BASE,
    },
    {
      label: "Games won",
      value: formatOptional(allTime.totalGamesWon, formatInteger),
      series: series.gamesWon,
      color: GAMES_COLOR,
      minBase: COUNT_BASE,
    },
    {
      label: "Reward amount",
      value: formatOptional(allTime.totalRewardAmount, kr),
      series: series.rewardAmount,
      color: MONEY_COLOR,
      minBase: AMOUNT_BASE,
    },
    {
      // The only measure with no all-time counterpart in the report, so this
      // tile shows the latest complete month and says so.
      label: "New users",
      value: formatOptional(latestValue(series.newUsers), formatInteger),
      series: series.newUsers,
      color: PEOPLE_COLOR,
      minBase: COUNT_BASE,
      caption: comparison ?? undefined,
    },
    // App-side measures. They have no all-time total and only two months of
    // history, so each shows the latest complete month and captions itself
    // with it. Bounce rate is left out for the same reason as games lost:
    // it rising is bad news, and a green arrow would say the opposite.
    {
      label: "Unique visitors",
      value: formatOptional(
        latestValue(activitySeries(activity, today, (a) => a.activeUsers)),
        formatInteger
      ),
      series: activitySeries(activity, today, (a) => a.activeUsers),
      color: ACTIVITY_COLOR,
      minBase: COUNT_BASE,
      caption: comparison ?? undefined,
    },
    {
      label: "Sessions",
      value: formatOptional(
        latestValue(activitySeries(activity, today, (a) => a.totalSessions)),
        formatInteger
      ),
      series: activitySeries(activity, today, (a) => a.totalSessions),
      color: ACTIVITY_COLOR,
      minBase: COUNT_BASE,
      caption: comparison ?? undefined,
    },
    {
      label: "Avg session",
      value: formatOptional(
        latestValue(
          activitySeries(activity, today, (a) => a.avgSessionDurationSeconds)
        ),
        formatSessionDuration
      ),
      series: activitySeries(activity, today, (a) => a.avgSessionDurationSeconds),
      color: ACTIVITY_COLOR,
      minBase: SECONDS_BASE,
      caption: comparison ?? undefined,
    },
    {
      label: "Stickiness (DAU/MAU)",
      value: formatOptional(
        latestValue(activitySeries(activity, today, (a) => a.stickinessPercent)),
        (v) => `${v.toFixed(1)}%`
      ),
      series: activitySeries(activity, today, (a) => a.stickinessPercent),
      color: ACTIVITY_COLOR,
      minBase: PERCENT_BASE,
      caption: comparison ?? undefined,
    },
  ];

  const tiles = selectPositiveFirst(candidates, TILE_COUNT);

  return (
    <section className="mb-8">
      <div className="flex items-baseline justify-between gap-4 mb-3">
        <h2 className="text-sm font-medium text-gray-500">Platform figures</h2>
        {/* The charts read at a glance; the exact month-by-month numbers behind
            them live in the KPI report, which is this dashboard's table view. */}
        <Link
          to={ROUTES.ADMIN_KPI_REPORT()}
          className="text-sm text-primary hover:underline"
        >
          Full KPI report
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        {tiles.map((tile) => (
          <StatTile
            key={tile.label}
            label={tile.label}
            value={tile.value}
            series={tile.series}
            color={tile.color}
            caption={tile.caption ?? caption}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TrendCard
          title="Transaction amount per month"
          series={series.amount}
          color={SERIES_COLORS[0]}
          formatValue={kr}
          formatTick={krTick}
          note={chartNote}
        />
        <TrendCard
          title="New users per month"
          series={series.newUsers}
          color={SERIES_COLORS[1]}
          formatValue={formatInteger}
          note={chartNote}
        />
      </div>
    </section>
  );
};

export default KpiHighlights;
